import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Bell, BellOff, Clock, Trophy, Flame, 
  CheckCircle, Loader2, Send
} from 'lucide-react';
import {
  isPushSupported,
  isPushSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  getNotificationPreferences,
  updateNotificationPreferences,
  sendTestNotification
} from '@/utils/pushNotifications';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    daily_reminder: true,
    streak_alert: true,
    achievement_alert: true,
    reminder_time: '09:00'
  });

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const isSupported = isPushSupported();
      setSupported(isSupported);
      
      if (isSupported) {
        const isSubscribed = await isPushSubscribed();
        setSubscribed(isSubscribed);
        
        if (isSubscribed) {
          const prefs = await getNotificationPreferences();
          setPreferences(prefs);
        }
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSubscription = async () => {
    setSaving(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success('Notificaciones desactivadas');
      } else {
        await subscribeToPush();
        setSubscribed(true);
        toast.success('¡Notificaciones activadas!');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.message === 'Notification permission denied') {
        toast.error('Debes permitir las notificaciones en tu navegador');
      } else {
        toast.error('Error al cambiar las notificaciones');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    try {
      await updateNotificationPreferences(newPrefs);
      toast.success('Preferencias guardadas');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error al guardar preferencias');
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      await sendTestNotification();
      toast.success('Notificación de prueba enviada');
    } catch (error) {
      console.error('Test notification error:', error);
      toast.error('Error al enviar notificación de prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const timeOptions = [
    '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
    '19:00', '20:00', '21:00'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 tracking-tight mb-2">
            Notificaciones
          </h1>
          <p className="text-slate-600">
            Configura los recordatorios para mantener tu racha de práctica
          </p>
        </div>

        {!supported ? (
          <div className="bg-orange-50 border border-orange-200 p-6 rounded-sm animate-fade-in-up">
            <div className="flex items-start gap-3">
              <BellOff className="w-6 h-6 text-orange-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  Notificaciones no soportadas
                </h3>
                <p className="text-sm text-slate-600">
                  Tu navegador no soporta notificaciones push. Prueba con Chrome, Firefox o Edge en escritorio.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Main Toggle */}
            <div className="bg-white border border-slate-200 p-6 animate-fade-in-up">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    subscribed ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    {subscribed ? (
                      <Bell className="w-6 h-6 text-green-600" />
                    ) : (
                      <BellOff className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Notificaciones Push
                    </h3>
                    <p className="text-sm text-slate-500">
                      {subscribed 
                        ? 'Recibirás recordatorios en tu dispositivo' 
                        : 'Activa para recibir recordatorios'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleToggleSubscription}
                  disabled={saving}
                  className={`rounded-full ${
                    subscribed 
                      ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' 
                      : 'bg-orange-600 hover:bg-orange-500 text-white'
                  }`}
                  data-testid="toggle-notifications-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : subscribed ? (
                    'Desactivar'
                  ) : (
                    'Activar'
                  )}
                </Button>
              </div>
            </div>

            {/* Preferences */}
            {subscribed && (
              <>
                <div className="bg-white border border-slate-200 p-6 space-y-6 animate-fade-in-up animation-delay-100">
                  <h3 className="font-semibold text-slate-900">
                    Tipos de notificaciones
                  </h3>
                  
                  {/* Daily Reminder */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <div>
                        <Label className="font-medium text-slate-900">
                          Recordatorio diario
                        </Label>
                        <p className="text-sm text-slate-500">
                          Te recordamos practicar cada día
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.daily_reminder}
                      onCheckedChange={(checked) => handlePreferenceChange('daily_reminder', checked)}
                      data-testid="daily-reminder-switch"
                    />
                  </div>

                  {/* Streak Alert */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Flame className="w-5 h-5 text-orange-500" />
                      <div>
                        <Label className="font-medium text-slate-900">
                          Alerta de racha
                        </Label>
                        <p className="text-sm text-slate-500">
                          Aviso si tu racha está en peligro
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.streak_alert}
                      onCheckedChange={(checked) => handlePreferenceChange('streak_alert', checked)}
                      data-testid="streak-alert-switch"
                    />
                  </div>

                  {/* Achievement Alert */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      <div>
                        <Label className="font-medium text-slate-900">
                          Logros desbloqueados
                        </Label>
                        <p className="text-sm text-slate-500">
                          Notificación cuando ganes un logro
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.achievement_alert}
                      onCheckedChange={(checked) => handlePreferenceChange('achievement_alert', checked)}
                      data-testid="achievement-alert-switch"
                    />
                  </div>
                </div>

                {/* Reminder Time */}
                {preferences.daily_reminder && (
                  <div className="bg-white border border-slate-200 p-6 animate-fade-in-up animation-delay-200">
                    <h3 className="font-semibold text-slate-900 mb-4">
                      Hora del recordatorio
                    </h3>
                    <div className="flex items-center gap-4">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <Select
                        value={preferences.reminder_time}
                        onValueChange={(value) => handlePreferenceChange('reminder_time', value)}
                      >
                        <SelectTrigger className="w-32" data-testid="reminder-time-select">
                          <SelectValue placeholder="Hora" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-slate-500">
                        Hora local
                      </span>
                    </div>
                  </div>
                )}

                {/* Test Notification */}
                <div className="bg-white border border-slate-200 p-6 animate-fade-in-up animation-delay-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        Probar notificaciones
                      </h3>
                      <p className="text-sm text-slate-500">
                        Envía una notificación de prueba
                      </p>
                    </div>
                    <Button
                      onClick={handleSendTest}
                      disabled={sendingTest}
                      variant="outline"
                      className="rounded-full"
                      data-testid="send-test-btn"
                    >
                      {sendingTest ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar prueba
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-100 p-4 rounded-sm animate-fade-in-up animation-delay-400">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Las notificaciones te ayudan a mantener una rutina de estudio consistente. 
                  Los usuarios con notificaciones activas tienen un 40% más de probabilidad de mantener su racha.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
